import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { DropOffBlock, itemNeedsDropOff } from "@/components/DropOffChecklist";
import { trackPixelEvent } from "@/components/MetaPixel";
import { trackOrder } from "@/lib/api";

const PaymentResult = () => {
  const [params] = useSearchParams();
  const status = params.get("status");
  const orderId = params.get("order_id") || "";
  const { clear } = useCart();
  const success = status === "success";
  const [dropOff, setDropOff] = useState(null);

  useEffect(() => {
    if (success) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (!success || !orderId) return;
    trackOrder(orderId)
      .then((order) => {
        trackPixelEvent("Purchase", {
          value: order.total,
          currency: "JMD",
          order_id: orderId,
        });
        if ((order.items || []).some(itemNeedsDropOff)) {
          setDropOff((order.items || []).some((i) => /sneaker/i.test(i.name || "")));
        }
      })
      .catch(() => {});
  }, [success, orderId]);

  return (
    <div
      data-testid="payment-result"
      className="mx-auto max-w-2xl px-4 pb-32 pt-48 text-center sm:px-6"
    >
      <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        {success ? (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-brand-cyan" />
            <h1 className="mt-6 font-display text-5xl font-black uppercase tracking-tight text-white">
              Payment Confirmed
            </h1>
            <p className="mt-3 font-mono text-sm uppercase tracking-[0.3em] text-brand-yellow">
              {orderId}
            </p>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
              WiPay has authorized your card in JMD. Your order is in the production queue — track
              it live as it moves through the studio.
            </p>
            {dropOff !== null && (
              <div className="mx-auto mt-6 max-w-md">
                <DropOffBlock sneaker={dropOff} />
              </div>
            )}
            <div className="mt-8 flex justify-center gap-4">
              <Link
                to="/track"
                data-testid="payment-result-track"
                className="bg-brand-yellow px-8 py-4 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
              >
                Track Order
              </Link>
              <Link
                to="/"
                className="border border-white/25 px-8 py-4 font-display text-base font-black uppercase tracking-widest text-white transition-colors hover:border-brand-magenta hover:text-brand-magenta"
              >
                Keep Shopping
              </Link>
            </div>
          </>
        ) : (
          <>
            <XCircle className="mx-auto h-16 w-16 text-brand-magenta" />
            <h1 className="mt-6 font-display text-5xl font-black uppercase tracking-tight text-white">
              Payment Not Completed
            </h1>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
              The WiPay session ended without a confirmed charge. Your cart is still saved — head
              back to checkout to try again, or WhatsApp us and we'll sort it out.
            </p>
            <Link
              to="/checkout"
              data-testid="payment-result-retry"
              className="mt-8 inline-block bg-brand-yellow px-8 py-4 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
            >
              Return to Checkout
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentResult;
